import { Component, Inject, OnInit, PLATFORM_ID } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router, RouterLink } from '@angular/router';
import { LoginService } from '../../core/services/login.service';
import { DoctorProfileService } from '../../core/services/doctor-profile.service';
import { DoctorProfileStatus } from '../../core/enums/doctor-profile-status.enum';
import { NotificationService } from '../../core/services/notification.service';
import { isPlatformBrowser } from '@angular/common';
import { PermissionService } from '../../core/services/permission.service';
import { UserRole, UserRolePermissions } from '../../core/enums/user-role.enum';
import { ClinicService } from '../../core/services/clinic.service';
import { LoginResponseModel } from '../../core/models/login-response.model';

@Component({
  selector: 'login',
  templateUrl: './login-component.html',
  imports: [ReactiveFormsModule, RouterLink],
  styleUrls: ['./login-component.scss'],
})
export class LoginComponent implements OnInit {
  loginForm: FormGroup;

  // UI state for password visibility
  private showPassword = false;
  isLoading = false;
  errorMessage: string | null = null;

  constructor(
    private fb: FormBuilder,
    private loginService: LoginService,
    private notification: NotificationService,
    private router: Router,
    @Inject(PLATFORM_ID) private platformId: Object,
    private permissionService: PermissionService,
    private profileService: DoctorProfileService,
    private clinicService: ClinicService,
  ) {
    this.loginForm = this.fb.group({
      email: ['', [Validators.required, Validators.email]],
      password: ['', [Validators.required]],
    });
  }

  ngOnInit(): void {
    if (isPlatformBrowser(this.platformId)) {
      localStorage.clear();
    }
  }

  // Toggle password visibility
  toggle(field: string): void {
    if (field === 'password') {
      this.showPassword = !this.showPassword;
    }
  }

  // Check if password should be hidden
  hidePassword(): boolean {
    return !this.showPassword;
  }

  // Form submission
  submitLogin(): void {
    this.errorMessage = null;
    if (this.loginForm.valid) {
      this.isLoading = true;
      const { email, password } = this.loginForm.value;
      this.loginService.login(email, password).subscribe({
        next: (res) => {
          this.handleLoginResponse(res, email);
        },
        error: (error) => {
          this.isLoading = false;
          const msg =
            error?.error?.message ||
            error?.message ||
            'Invalid email or password. Please check your credentials and try again.';
          this.errorMessage = msg;
          this.notification.error(msg, 'Authentication Failed');
        },
      });
    } else {
      this.loginForm.markAllAsTouched();
    }
  }

  private handleLoginResponse(res: LoginResponseModel, email: string): void {
    if (res.requiresMfa) {
      if (res.mfaSessionId) {
        sessionStorage.setItem('mfa_session_id', res.mfaSessionId);
      }
      sessionStorage.setItem('mfa_email', res.email || email);
      this.isLoading = false;
      this.router.navigate(['/otp-login']);
      return;
    }

    if (res.idToken != null) {
      sessionStorage.setItem('access_token', res.idToken);
    }
    if (res.refreshToken != null) {
      localStorage.setItem('refresh_token', res.refreshToken);
    }
    this.getProfileDetails();
  }

  getProfileDetails(): void {
    this.profileService.getUserProfile().subscribe({
      next: (res) => {
        localStorage.setItem('userProfile', JSON.stringify(res));
        localStorage.setItem('availableUserRoles', JSON.stringify(res.roles));
        if (res.clinicId) {
          localStorage.setItem('clinicId', res.clinicId);
          localStorage.setItem('activeClinicId', res.clinicId);
          sessionStorage.setItem('clinicId', res.clinicId);
          sessionStorage.setItem('activeClinicId', res.clinicId);
        }

        if (res.currentActiveRole == 'DOCTOR' || res.roles[0] === 'DOCTOR') {
          localStorage.setItem('userRole', 'DOCTOR');
          this.permissionService.setPermissions(UserRolePermissions[UserRole.DOCTOR]);
          this.getDoctorProfileDetails();
        } else if (res.currentActiveRole == 'MANAGER' || res.roles[0] === 'MANAGER') {
          localStorage.setItem('userRole', 'MANAGER');
          this.permissionService.setPermissions(UserRolePermissions[UserRole.MANAGER]);
          this.router.navigate(['dashboard/manager-dashboard'], {
            state: { managerData: res },
          });
        } else if (res.roles[0] === 'RECEPTION') {
          localStorage.setItem('userRole', 'RECEPTION');
          this.permissionService.setPermissions(UserRolePermissions[UserRole.RECEPTION]);
          this.getClinicDoctorsForReception(res);
        }
      },
      error: (error) => {
        this.isLoading = false;
        const msg = error?.error?.message || 'Failed to retrieve profile details.';
        this.errorMessage = msg;
        this.notification.error(msg, 'Profile Error');
      },
    });
  }

  getClinicDoctorsForReception(receptionProfile: any): void {
    this.clinicService.getClinicDoctorProfiles().subscribe({
      next: (doctors) => {
        localStorage.setItem('clinicDoctors', JSON.stringify(doctors));
        this.router.navigate(['dashboard/reception-dashboard'], {
          state: { receptionData: receptionProfile },
        });
      },
      error: () => {
        this.router.navigate(['dashboard/reception-dashboard'], {
          state: { receptionData: receptionProfile },
        });
      },
    });
  }

  getDoctorProfileDetails(): void {
    this.profileService.getDoctorProfileDetails().subscribe({
      next: (res) => {
        this.isLoading = false;
        if (res.profileStatus === DoctorProfileStatus.DOCTOR_NOT_ONBOARDED) {
          localStorage.setItem('doctorLoad', JSON.stringify(res));
          this.router.navigate(['dashboard/doctor-onboard']);
        } else {
          this.router.navigate(['dashboard/doctor-dashboard'], {
            state: { doctorData: res },
          });
        }
      },
      error: (error) => {
        this.isLoading = false;
        const msg = error?.error?.message || 'Failed to load doctor profile.';
        this.errorMessage = msg;
        this.notification.error(msg, 'Doctor Profile Error');
      },
    });
  }
}
